/**
 * What a MINIMAL VALID artifact earns, per rubric category.
 *
 * Some fixes are foundational: the artifact does not exist at all, so the
 * tool's action generator has nothing to improve and stays silent. Creating
 * the file is an obvious fix, but creating it does NOT earn the full rubric —
 * the file still has to be good. These maps record only the points a minimal,
 * spec-compliant artifact earns on structure and well-formedness alone. Every
 * remaining point stays GATED behind the artifact existing.
 *
 * ANTI-DRIFT: these numbers are asserted against the REAL scorer in
 * projection.test.ts, which builds an actual minimal file and checks that it
 * scores at least this much in each listed category. Reweight the rubric and
 * the test fails rather than the roadmap quietly over-promising.
 *
 * Deliberately conservative. A minimal llms.txt with two links measures 26
 * (Structure 17, Links & URLs 9); with a single link it measures 24. We claim
 * the floor, not the better case.
 */

import type { TierOneKey } from '@/lib/platform/derive-tool-scores'

export const MINIMAL_VALID_BASELINE: Partial<
  Record<TierOneKey, Record<string, number>>
> = {
  llms: {
    // H1 +5, blockquote summary +5, one section +2, clean markdown +5
    Structure: 17,
    // At least one URL +5, links present in one section +2. The 5+ and 10+
    // volume bonuses are content depth, not existence, so they stay gated.
    'Links & URLs': 7,
  },
}

/** Total baseline a foundational fix recovers for a tool, before capping. */
export function baselineTotal(tool: TierOneKey): number {
  const map = MINIMAL_VALID_BASELINE[tool]
  if (!map) return 0
  return Object.values(map).reduce((sum, n) => sum + n, 0)
}
